"use client"

import type React from "react"
import { useState, useEffect, useMemo } from "react"
import {
  ShoppingCart,
  Users,
  ArrowUpRight,
  ArrowDownRight,
  TrendingUp,
  Clock,
  CheckCircle,
  Download,
  Star,
  AlertCircle,
  ArrowUp,
  ArrowDown,
  Activity,
  Plus,
  List,
  Settings,
  Bell,
  X,
  ImageIcon,
  Eye,
  EyeOff,
  Edit,
  RefreshCw,
} from "lucide-react"
import { productsService, ordersService, categoriesService as firebaseCategoriesService } from "../../services/firebase"
import { userService } from "../../services/userService"
import type { Product, User as AppUser, Category } from "../../types"
import StatCards from "../../components/admin/dashboard/StatCards"
import toast from "react-hot-toast"
import * as XLSX from "xlsx"
import jsPDF from "jspdf"
import html2canvas from "html2canvas"
import { useNavigate, Link } from "react-router-dom"
import { useSelector } from "react-redux"
import type { RootState } from "../../store/store"
import { DndContext, closestCenter } from "@dnd-kit/core"
import { arrayMove, SortableContext, verticalListSortingStrategy, useSortable } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import ErrorBoundary from "../../components/common/ErrorBoundary"
import type { UserProfile } from "../../services/userService"
import type { Order as ReduxOrder } from "../../store/slices/ordersSlice"
import { formatDistanceToNow } from "date-fns"

type TimeRange = "24h" | "7d" | "30d" | "90d" | "1y" | "all"
type OrderStatus = "all" | "pending" | "processing" | "shipped" | "delivered" | "cancelled"

interface Notification {
  id: string
  type: "new_order" | "order_update" | "low_stock" | "system"
  title: string
  message: string
  timestamp: Date
  read: boolean
  data?: any
}

// Banner interface for hero carousel management
interface Banner {
  id: string
  title: string
  subtitle: string
  description: string
  image: string
  ctaText: string
  ctaLink: string
  isActive: boolean
  order: number
}

// Widget config - Updated to include hero banners
const DEFAULT_WIDGETS = [
  { id: "statCards", label: "Stat Cards", visible: true },
  { id: "performanceMetrics", label: "Performance Metrics", visible: true },
  { id: "heroBanners", label: "Hero Banners", visible: true },
  { id: "conversionFunnel", label: "Conversion Funnel", visible: true },
  { id: "customerSegmentation", label: "Customer Segmentation", visible: true },
  { id: "topProducts", label: "Top Products", visible: true },
  { id: "topCustomers", label: "Top Customers", visible: true },
  { id: "recentOrders", label: "Recent Orders", visible: true },
]

function getSavedWidgets() {
  try {
    const saved = localStorage.getItem("dashboardWidgets")
    if (saved) return JSON.parse(saved)
  } catch (e) {
    console.error("Failed to parse saved widgets from localStorage", e)
  }
  return DEFAULT_WIDGETS
}

function saveWidgets(widgets: any) {
  localStorage.setItem("dashboardWidgets", JSON.stringify(widgets))
}

function SortableWidgetItem({ id, label, visible, onToggle, listeners, attributes, isDragging }: any) {
  return (
    <div
      className={`flex items-center justify-between p-2 rounded border mb-2 bg-white shadow-sm ${isDragging ? "opacity-60" : ""}`}
      {...attributes}
      {...listeners}
    >
      <span className="cursor-move mr-2">☰</span>
      <span className="flex-1">{label}</span>
      <label className="flex items-center gap-2">
        <input type="checkbox" checked={visible} onChange={onToggle} /> Show
      </label>
    </div>
  )
}

function SortableWidgetList({ widgets, setWidgets }: any) {
  return (
    <SortableContext items={widgets.map((w: any) => w.id)} strategy={verticalListSortingStrategy}>
      {widgets.map((widget: any) => (
        <SortableWidget key={widget.id} widget={widget} setWidgets={setWidgets} />
      ))}
    </SortableContext>
  )
}

function SortableWidget({ widget, setWidgets }: any) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: widget.id })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }
  return (
    <div ref={setNodeRef} style={style}>
      <SortableWidgetItem
        {...widget}
        onToggle={() =>
          setWidgets((prev: any) => prev.map((w: any) => (w.id === widget.id ? { ...w, visible: !w.visible } : w)))
        }
        listeners={listeners}
        attributes={attributes}
        isDragging={isDragging}
      />
    </div>
  )
}

// Notification Bell Component
const NotificationBell: React.FC<{
  notifications: Notification[]
  unreadCount: number
  markAsRead: (id: string) => void
  markAllAsRead: () => void
  clearNotification: (id: string) => void
}> = ({ notifications, unreadCount, markAsRead, markAllAsRead, clearNotification }) => {
  const [isOpen, setIsOpen] = useState(false)

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case "new_order":
        return <ShoppingCart className="w-5 h-5 text-green-500" />
      case "order_update":
        return <Clock className="w-5 h-5 text-blue-500" />
      case "low_stock":
        return <AlertCircle className="w-5 h-5 text-orange-500" />
      default:
        return <Settings className="w-5 h-5 text-gray-500" />
    }
  }

  return (
    <div className="relative">
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-600 hover:text-gray-800 focus:outline-none"
      >
        <Bell className="w-6 h-6" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {/* Notification Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-96 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Notifications</h3>
            {unreadCount > 0 && (
              <button onClick={markAllAsRead} className="text-sm text-blue-600 hover:text-blue-800 font-medium">
                Mark all as read
              </button>
            )}
          </div>

          {/* Notifications List */}
          <div className="max-h-72 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Bell className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <p>No notifications yet</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`p-4 hover:bg-gray-50 transition-colors cursor-pointer ${
                      !notification.read ? "bg-blue-50 border-l-4 border-blue-500" : ""
                    }`}
                    onClick={() => !notification.read && markAsRead(notification.id)}
                  >
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0">{getNotificationIcon(notification.type)}</div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">{notification.title}</p>
                        <p className="text-sm text-gray-600 mt-1">{notification.message}</p>
                        <p className="text-xs text-gray-400 mt-2">
                          {formatDistanceToNow(notification.timestamp, { addSuffix: true })}
                        </p>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          clearNotification(notification.id)
                        }}
                        className="flex-shrink-0 text-gray-400 hover:text-gray-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="p-3 border-t border-gray-200 text-center">
              <button
                onClick={() => setIsOpen(false)}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                Close notifications
              </button>
            </div>
          )}
        </div>
      )}

      {/* Backdrop to close dropdown */}
      {isOpen && <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />}
    </div>
  )
}

// Hero Banner Management Component
const HeroBannerManager: React.FC = () => {
  const [banners, setBanners] = useState<Banner[]>([])
  const [editingBanner, setEditingBanner] = useState<Banner | null>(null)
  const [showAddForm, setShowAddForm] = useState(false)

  // Load banners from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem("heroBanners")
      if (saved) {
        setBanners(JSON.parse(saved))
      } else {
        // Initialize with default banners
        const defaultBanners: Banner[] = [
          {
            id: "default-1",
            title: "Transform Your",
            subtitle: "Home Today",
            description:
              "Discover premium furniture that combines style, comfort, and quality. Create the perfect ambiance for your dream home with our curated collection.",
            image: "https://images.pexels.com/photos/1571460/pexels-photo-1571460.jpeg?auto=compress&cs=tinysrgb&w=800",
            ctaText: "Shop Now",
            ctaLink: "/categories",
            isActive: true,
            order: 1,
          },
          {
            id: "default-2",
            title: "Premium Quality",
            subtitle: "Furniture Collection",
            description:
              "Experience luxury living with our handpicked selection of premium furniture. From modern designs to classic styles, find the perfect pieces for every room.",
            image: "https://images.pexels.com/photos/1743229/pexels-photo-1743229.jpeg?auto=compress&cs=tinysrgb&w=800",
            ctaText: "Explore Collection",
            ctaLink: "/products",
            isActive: true,
            order: 2,
          },
          {
            id: "default-3",
            title: "Special Offers",
            subtitle: "Up to 50% Off",
            description:
              "Don't miss our exclusive deals on select furniture items. Limited time offer with free delivery and extended warranty on premium collections.",
            image: "https://images.pexels.com/photos/1350789/pexels-photo-1350789.jpeg?auto=compress&cs=tinysrgb&w=800",
            ctaText: "View Offers",
            ctaLink: "/products?sale=true",
            isActive: true,
            order: 3,
          },
        ]
        setBanners(defaultBanners)
        localStorage.setItem("heroBanners", JSON.stringify(defaultBanners))
      }
    } catch (error) {
      console.error("Error loading banners:", error)
    }
  }, [])

  // Save banners to localStorage
  const saveBanners = (updatedBanners: Banner[]) => {
    setBanners(updatedBanners)
    localStorage.setItem("heroBanners", JSON.stringify(updatedBanners))
    toast.success("Banners updated successfully!")
  }

  // Add new banner
  const addBanner = (bannerData: Omit<Banner, "id" | "order">) => {
    const newBanner: Banner = {
      ...bannerData,
      id: Date.now().toString(),
      order: banners.length + 1,
    }
    saveBanners([...banners, newBanner])
    setShowAddForm(false)
  }

  // Update banner
  const updateBanner = (id: string, updates: Partial<Banner>) => {
    const updatedBanners = banners.map((banner) => (banner.id === id ? { ...banner, ...updates } : banner))
    saveBanners(updatedBanners)
    setEditingBanner(null)
  }

  // Delete banner
  const deleteBanner = (id: string) => {
    if (banners.length <= 1) {
      toast.error("At least one banner is required")
      return
    }
    const updatedBanners = banners.filter((banner) => banner.id !== id)
    saveBanners(updatedBanners)
  }

  // Reorder banners
  const reorderBanners = (startIndex: number, endIndex: number) => {
    const reorderedBanners = [...banners]
    const [removed] = reorderedBanners.splice(startIndex, 1)
    reorderedBanners.splice(endIndex, 0, removed)

    // Update order numbers
    const updatedBanners = reorderedBanners.map((banner, index) => ({
      ...banner,
      order: index + 1,
    }))

    saveBanners(updatedBanners)
  }

  const BannerForm: React.FC<{
    banner?: Banner
    onSave: (banner: Omit<Banner, "id" | "order">) => void
    onCancel: () => void
  }> = ({ banner, onSave, onCancel }) => {
    const [formData, setFormData] = useState({
      title: banner?.title || "",
      subtitle: banner?.subtitle || "",
      description: banner?.description || "",
      image: banner?.image || "",
      ctaText: banner?.ctaText || "Shop Now",
      ctaLink: banner?.ctaLink || "/categories",
      isActive: banner?.isActive ?? true,
    })

    const validateImageUrl = (url: string): boolean => {
      if (!url) return false
      try {
        new URL(url)
        return /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(url)
      } catch {
        return false
      }
    }

    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault()
      if (!formData.title || !formData.subtitle || !formData.description || !formData.image) {
        toast.error("Please fill in all required fields")
        return
      }
      if (!validateImageUrl(formData.image)) {
        toast.error("Please provide a valid image URL")
        return
      }
      onSave(formData)
    }

    return (
      <form onSubmit={handleSubmit} className="space-y-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              placeholder="Transform Your"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle *</label>
            <input
              type="text"
              value={formData.subtitle}
              onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              placeholder="Home Today"
              required
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            rows={3}
            placeholder="Discover premium furniture that combines style, comfort, and quality..."
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Image URL *</label>
          <input
            type="url"
            value={formData.image}
            onChange={(e) => setFormData({ ...formData, image: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            placeholder="https://images.pexels.com/photos/..."
            required
          />
          {formData.image && validateImageUrl(formData.image) && (
            <div className="mt-2">
              <img
                src={formData.image || "/placeholder.svg"}
                alt="Banner preview"
                className="w-full h-32 object-cover rounded-lg border border-gray-200"
                onError={() => toast.error("Failed to load image")}
              />
            </div>
          )}
          {formData.image && !validateImageUrl(formData.image) && (
            <p className="text-sm text-red-600 mt-1">
              Please provide a valid image URL (jpg, jpeg, png, gif, webp, svg)
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">CTA Button Text</label>
            <input
              type="text"
              value={formData.ctaText}
              onChange={(e) => setFormData({ ...formData, ctaText: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              placeholder="Shop Now"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">CTA Link</label>
            <input
              type="text"
              value={formData.ctaLink}
              onChange={(e) => setFormData({ ...formData, ctaLink: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              placeholder="/categories"
            />
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="isActive"
            checked={formData.isActive}
            onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
            className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
          />
          <label htmlFor="isActive" className="text-sm font-medium text-gray-700">
            Active (show on homepage)
          </label>
        </div>

        {/* Add this before the action buttons in BannerForm */}
        {formData.title &&
          formData.subtitle &&
          formData.description &&
          formData.image &&
          validateImageUrl(formData.image) && (
            <BannerPreview
              banner={{
                id: "preview",
                title: formData.title,
                subtitle: formData.subtitle,
                description: formData.description,
                image: formData.image,
                ctaText: formData.ctaText,
                ctaLink: formData.ctaLink,
                isActive: formData.isActive,
                order: 0,
              }}
            />
          )}

        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button type="submit" className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">
            {banner ? "Update Banner" : "Add Banner"}
          </button>
        </div>
      </form>
    )
  }

  // Add this after the existing BannerForm component, before the return statement of HeroBannerManager
  const BannerPreview: React.FC<{ banner: Banner }> = ({ banner }) => {
    return (
      <div className="mt-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
        <h5 className="text-sm font-medium text-gray-700 mb-2">Homepage Preview:</h5>
        <div className="relative bg-gradient-to-br from-primary-50 to-primary-100 rounded-lg overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-center p-6">
            <div className="space-y-3">
              <h1 className="text-2xl font-bold text-primary-900">
                {banner.title}
                <span className="text-primary-600 block">{banner.subtitle}</span>
              </h1>
              <p className="text-sm text-gray-600">{banner.description}</p>
              <div className="flex space-x-2">
                <span className="bg-primary-600 text-white px-3 py-1 rounded text-xs">{banner.ctaText}</span>
                <span className="border border-primary-600 text-primary-600 px-3 py-1 rounded text-xs">
                  Browse Categories
                </span>
              </div>
            </div>
            <div className="relative">
              <img
                src={banner.image || "/placeholder.svg"}
                alt={`${banner.title} - ${banner.subtitle}`}
                className="rounded-lg w-full h-32 object-cover"
                onError={(e) => {
                  e.currentTarget.src = "/placeholder.svg?height=128&width=200&text=Preview"
                }}
              />
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
      {/* Update the header section to include sync status */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <ImageIcon className="w-6 h-6 text-primary-600" />
          <div>
            <h3 className="text-xl font-semibold text-gray-900">Hero Banners</h3>
            <p className="text-sm text-gray-500">
              Changes sync automatically to homepage • {banners.filter((b) => b.isActive).length} active banners
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => {
              // Force refresh the homepage data
              const event = new CustomEvent("heroBannersUpdated", { detail: banners })
              window.dispatchEvent(event)
              toast.success("Homepage banners refreshed!")
            }}
            className="flex items-center space-x-2 px-3 py-2 text-primary-600 border border-primary-200 rounded-lg hover:bg-primary-50 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Refresh Homepage</span>
          </button>
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>Add Banner</span>
          </button>
        </div>
      </div>

      {/* Add Banner Form */}
      {showAddForm && (
        <div className="mb-6">
          <h4 className="text-lg font-medium text-gray-900 mb-4">Add New Banner</h4>
          <BannerForm onSave={addBanner} onCancel={() => setShowAddForm(false)} />
        </div>
      )}

      {/* Banners List */}
      <div className="space-y-4">
        {banners
          .sort((a, b) => a.order - b.order)
          .map((banner, index) => (
            <div
              key={banner.id}
              className={`border rounded-lg p-4 ${
                banner.isActive ? "border-green-200 bg-green-50" : "border-gray-200 bg-gray-50"
              }`}
            >
              {editingBanner?.id === banner.id ? (
                <BannerForm
                  banner={banner}
                  onSave={(data) => updateBanner(banner.id, data)}
                  onCancel={() => setEditingBanner(null)}
                />
              ) : (
                <div className="flex items-start space-x-4">
                  {/* Banner Image */}
                  <div className="flex-shrink-0">
                    <img
                      src={banner.image || "/placeholder.svg"}
                      alt={`${banner.title} ${banner.subtitle}`}
                      className="w-24 h-16 object-cover rounded-lg border border-gray-200"
                      onError={(e) => {
                        e.currentTarget.src = "/placeholder.svg"
                      }}
                    />
                  </div>

                  {/* Banner Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900">
                          {banner.title} <span className="text-primary-600">{banner.subtitle}</span>
                        </h4>
                        <p className="text-sm text-gray-600 mt-1 line-clamp-2">{banner.description}</p>
                        <div className="flex items-center space-x-4 mt-2">
                          <span className="text-xs text-gray-500">
                            CTA: {banner.ctaText} → {banner.ctaLink}
                          </span>
                          <span className="text-xs text-gray-500">Order: {banner.order}</span>
                          {/* Add this after the banner order span */}
                          <span
                            className={`text-xs px-2 py-1 rounded-full ${
                              banner.isActive ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600"
                            }`}
                          >
                            {banner.isActive ? "🔗 Live on Homepage" : "⏸️ Hidden"}
                          </span>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center space-x-2 flex-shrink-0">
                        <button
                          onClick={() => updateBanner(banner.id, { isActive: !banner.isActive })}
                          className={`p-2 rounded-lg transition-colors ${
                            banner.isActive ? "text-green-600 hover:bg-green-100" : "text-gray-400 hover:bg-gray-100"
                          }`}
                          title={banner.isActive ? "Hide banner" : "Show banner"}
                        >
                          {banner.isActive ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                        </button>

                        <button
                          onClick={() => setEditingBanner(banner)}
                          className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                          title="Edit banner"
                        >
                          <Edit className="w-4 h-4" />
                        </button>

                        {index > 0 && (
                          <button
                            onClick={() => reorderBanners(index, index - 1)}
                            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Move up"
                          >
                            <ArrowUp className="w-4 h-4" />
                          </button>
                        )}

                        {index < banners.length - 1 && (
                          <button
                            onClick={() => reorderBanners(index, index + 1)}
                            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Move down"
                          >
                            <ArrowDown className="w-4 h-4" />
                          </button>
                        )}

                        <button
                          onClick={() => {
                            if (window.confirm("Are you sure you want to delete this banner?")) {
                              deleteBanner(banner.id)
                            }
                          }}
                          className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                          title="Delete banner"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}

        {banners.length === 0 && (
          <div className="text-center py-8">
            <ImageIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 mb-4">No banners configured</p>
            <button
              onClick={() => setShowAddForm(true)}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              Add Your First Banner
            </button>
          </div>
        )}
      </div>

      {/* Preview Link */}
      <div className="mt-6 pt-6 border-t border-gray-200">
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-600">
            Changes are saved automatically and will appear on the homepage immediately.
          </p>
          <Link
            to="/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center space-x-2 text-primary-600 hover:text-primary-700 font-medium"
          >
            <Eye className="w-4 h-4" />
            <span>Preview Homepage</span>
          </Link>
        </div>
      </div>
    </div>
  )
}

const AdminDashboardContent: React.FC = () => {
  const navigate = useNavigate()
  const { items: reduxProducts } = useSelector((state: RootState) => state.products)
  const [products, setProducts] = useState((reduxProducts as any[]) || [])
  const [orders, setOrders] = useState<Order[]>([])
  const [users, setUsers] = useState<AppUser[]>([])
  const [allCategories, setAllCategories] = useState<Category[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Notification state
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [lastOrderCount, setLastOrderCount] = useState<number>(0)

  const [stats, setStats] = useState({
    totalSales: 0,
    todaySales: 0,
    yesterdaySales: 0,
    totalOrders: 0,
    totalProducts: 0,
    totalUsers: 0,
    pendingOrders: 0,
    completedOrders: 0,
    lowStockProducts: 0,
    avgOrderValue: 0,
    conversionRate: 0,
    topSellingCategory: "N/A",
    monthlyGrowth: 0,
    dailyOrdersAvg: 0,
  })
  const [timeFilter, setTimeFilter] = useState("all")
  const [salesData, setSalesData] = useState<
    Array<{ date: string; sales: number; orders: number; customers: number; revenue: number }>
  >([])
  const [topProducts, setTopProducts] = useState<
    Array<{
      id: string
      name: string
      price: number
      reviews: number
      stock: number
      image: string
      totalSold: number
      revenue: number
    }>
  >([])
  const [topCustomers, setTopCustomers] = useState<
    Array<{
      id: string
      name: string
      email: string
      totalOrders: number
      totalSpent: number
    }>
  >([])
  const [customDateRange, setCustomDateRange] = useState<[Date | null, Date | null]>([null, null])
  const [selectedCategory, setSelectedCategory] = useState<string>("")
  const [selectedProduct, setSelectedProduct] = useState<string>("")

  const categories = useMemo(() => Array.from(new Set(products.flatMap((p) => p.categories || []))), [products])
  const productsByCategory = useMemo(
    () => (selectedCategory ? products.filter((p) => p.categories?.includes(selectedCategory)) : products),
    [products, selectedCategory],
  )

  // Notification functions
  const addNotification = (notification: Omit<Notification, "id" | "timestamp" | "read">) => {
    const newNotification: Notification = {
      ...notification,
      id: Date.now().toString(),
      timestamp: new Date(),
      read: false,
    }

    setNotifications((prev) => [newNotification, ...prev].slice(0, 50)) // Keep only last 50 notifications

    // Show toast notification
    if (notification.type === "new_order") {
      toast.success(notification.title, {
        duration: 4000,
        icon: "🛒",
      })
    }
  }

  const markAsRead = (id: string) => {
    setNotifications((prev) =>
      prev.map((notification) => (notification.id === id ? { ...notification, read: true } : notification)),
    )
  }

  const markAllAsRead = () => {
    setNotifications((prev) => prev.map((notification) => ({ ...notification, read: true })))
  }

  const clearNotification = (id: string) => {
    setNotifications((prev) => prev.filter((notification) => notification.id !== id))
  }

  const unreadCount = notifications.filter((n) => !n.read).length

  const mapUserProfileToUser = (profile: UserProfile): AppUser => ({
    uid: profile.id || "",
    email: profile.email,
    name: profile.name,
    role: profile.role || "user",
  })

  const mapReduxOrderToOrder = (order: ReduxOrder): any => ({
    id: order.id,
    userId: (order as any).userId,
    items: order.items.map((item: any) => ({
      productId: item.id,
      quantity: item.quantity,
      price: item.price,
      name: item.name,
    })),
    total: order.total,
    status: order.status,
    createdAt: order.createdAt,
    updatedAt: order.updatedAt,
    shippingAddress: order.shippingAddress,
    paymentMethod: order.paymentMethod,
    paymentStatus: order.paymentStatus,
  })

  useEffect(() => {
    const fetchAllData = async () => {
      setLoading(true)
      try {
        const ordersData = await ordersService.getOrders()
        const mappedOrders = ordersData.map(mapReduxOrderToOrder)
        setOrders(mappedOrders)

        if (!reduxProducts || reduxProducts.length === 0) {
          const productsData = await productsService.getProducts()
          setProducts(productsData as any)
        }
        const usersData = await userService.getAllUsers()
        setUsers(usersData.map(mapUserProfileToUser))
        const fetchedCategories = await firebaseCategoriesService.getCategories()
        setAllCategories(fetchedCategories.filter((cat) => cat.isActive))
        setError(null)

        // Check for new orders and add notifications
        if (lastOrderCount > 0 && mappedOrders.length > lastOrderCount) {
          const newOrdersCount = mappedOrders.length - lastOrderCount
          const latestOrders = mappedOrders.slice(0, newOrdersCount)

          latestOrders.forEach((order) => {
            addNotification({
              type: "new_order",
              title: "New Order Received!",
              message: `Order #${order.id.slice(-8)} for ₹${order.total.toLocaleString()} from ${order.shippingAddress?.name || "Customer"}`,
              data: { orderId: order.id, total: order.total, customerName: order.shippingAddress?.name },
            })
          })
        }

        setLastOrderCount(mappedOrders.length)
      } catch (error) {
        console.error("Error fetching data:", error)
        setError("Failed to load dashboard data")
      } finally {
        setLoading(false)
      }
    }
    fetchAllData()
  }, [reduxProducts, lastOrderCount])

  useEffect(() => {
    if (reduxProducts && reduxProducts.length > 0) {
      setProducts(reduxProducts)
    }
  }, [reduxProducts])

  const getFilteredOrders = (orders: Order[], timeFilter: string) => {
    let filtered = [...orders]
    const now = new Date()
    const today = now.toDateString()

    if (timeFilter === "custom" && customDateRange[0] && customDateRange[1]) {
      const [start, end] = customDateRange
      filtered = filtered.filter((order) => {
        const orderDate = new Date(order.createdAt)
        return orderDate >= start! && orderDate <= end!
      })
    } else {
      switch (timeFilter) {
        case "today":
          filtered = filtered.filter((order) => new Date(order.createdAt).toDateString() === today)
          break
        case "week": {
          const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
          filtered = filtered.filter((order) => new Date(order.createdAt) >= weekAgo)
          break
        }
        case "month": {
          const monthAgo = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate())
          filtered = filtered.filter((order) => new Date(order.createdAt) >= monthAgo)
          break
        }
        case "all":
        default:
          break
      }
    }

    if (selectedCategory) {
      filtered = filtered.filter((order) =>
        order.items.some((item) => {
          const product = products.find((p) => p.id === item.productId)
          return product && product.categories?.includes(selectedCategory)
        }),
      )
    }

    if (selectedProduct) {
      filtered = filtered.filter((order) => order.items.some((item) => item.productId === selectedProduct))
    }

    return filtered
  }

  useEffect(() => {
    if (orders.length === 0 && products.length === 0 && users.length === 0) return

    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1)

    const filteredOrders = getFilteredOrders(orders, timeFilter)
    const todayOrders = orders.filter(
      (order) =>
        new Date(order.createdAt) >= today &&
        new Date(order.createdAt) < new Date(today.getTime() + 24 * 60 * 60 * 1000),
    )
    const yesterdayOrders = orders.filter(
      (order) => new Date(order.createdAt) >= yesterday && new Date(order.createdAt) < today,
    )

    const totalSales = filteredOrders.reduce((sum, order) => sum + order.total, 0)
    const todaySales = todayOrders.reduce((sum, order) => sum + order.total, 0)
    const yesterdaySales = yesterdayOrders.reduce((sum, order) => sum + order.total, 0)
    const pendingOrders = filteredOrders.filter((order) => order.status === "pending").length
    const completedOrders = filteredOrders.filter((order) => order.status === "delivered").length
    const lowStockProducts = products.filter((product) => product.stock !== undefined && product.stock < 20).length
    const avgOrderValue = filteredOrders.length > 0 ? totalSales / filteredOrders.length : 0
    const conversionRate = users.length > 0 ? (filteredOrders.length / users.length) * 100 : 0
    const monthlyGrowth = yesterdaySales > 0 ? ((todaySales - yesterdaySales) / yesterdaySales) * 100 : 0
    const dailyOrdersAvg = orders.length > 0 ? orders.length / 30 : 0

    const topSellingCategory = (() => {
      const categorySales: { [key: string]: number } = {}
      filteredOrders.forEach((order) => {
        order.items.forEach((item) => {
          const product = products.find((p) => p.id === item.productId)
          if (product && product.categories) {
            product.categories.forEach((catSlug: string) => {
              const categoryName = allCategories.find((c) => c.slug === catSlug)?.name || catSlug
              categorySales[categoryName] = (categorySales[categoryName] || 0) + item.quantity * item.price
            })
          }
        })
      })
      const sortedCategories = Object.entries(categorySales).sort(([, a], [, b]) => b - a)
      return sortedCategories.length > 0 ? sortedCategories[0][0] : "N/A"
    })()

    setStats({
      totalSales,
      todaySales,
      yesterdaySales,
      totalOrders: filteredOrders.length,
      totalProducts: products.length,
      totalUsers: users.length,
      pendingOrders,
      completedOrders,
      lowStockProducts,
      avgOrderValue,
      conversionRate,
      topSellingCategory,
      monthlyGrowth,
      dailyOrdersAvg,
    })

    // Check for low stock notifications
    if (lowStockProducts > 0) {
      const existingLowStockNotifications = notifications.filter((n) => n.type === "low_stock" && !n.read)
      if (existingLowStockNotifications.length === 0) {
        addNotification({
          type: "low_stock",
          title: "Low Stock Alert",
          message: `${lowStockProducts} products have less than 20 items in stock`,
          data: { lowStockCount: lowStockProducts },
        })
      }
    }

    const salesByMonth = filteredOrders.reduce(
      (acc, order) => {
        const date = new Date(order.createdAt)
        const month = date.toLocaleString("default", { month: "short", year: "numeric" })
        if (!acc[month]) {
          acc[month] = { sales: 0, orders: 0, customers: new Set() }
        }
        acc[month].sales += order.total
        acc[month].orders += 1
        acc[month].customers.add((order as any).userId || "")
        return acc
      },
      {} as Record<string, { sales: number; orders: number; customers: Set<string> }>,
    )

    const salesChartData = Object.entries(salesByMonth)
      .map(([date, data]) => ({
        date,
        name: date,
        sales: data.sales,
        orders: data.orders,
        customers: data.customers.size,
        revenue: data.sales,
      }))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    setSalesData(salesChartData)

    const productSales = products
      .map((product) => {
        const productOrders = filteredOrders.filter((order) =>
          order.items.some((item) => item.productId === product.id),
        )
        const totalSold = productOrders.reduce((sum, order) => {
          const productItems = order.items.filter((item) => item.productId === product.id)
          return sum + productItems.reduce((itemSum, item) => itemSum + item.quantity, 0)
        }, 0)
        const revenue = productOrders.reduce((sum, order) => {
          const productItems = order.items.filter((item) => item.productId === product.id)
          return sum + productItems.reduce((itemSum, item) => itemSum + item.price * item.quantity, 0)
        }, 0)
        return {
          ...product,
          totalSold,
          revenue,
          image: product.images?.[0] || "/placeholder.svg",
        }
      })
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 5)
    setTopProducts(productSales)

    const customerStats = users
      .map((user) => {
        const userOrders = filteredOrders.filter((order) => (order as any).userId === user.uid)
        const totalSpent = userOrders.reduce((sum, order) => sum + order.total, 0)
        return {
          id: user.uid,
          name: user.name,
          email: user.email,
          totalOrders: userOrders.length,
          totalSpent,
        }
      })
      .sort((a, b) => b.totalSpent - a.totalSpent)
      .slice(0, 5)
    setTopCustomers(customerStats)
  }, [
    products,
    orders,
    users,
    timeFilter,
    customDateRange,
    selectedCategory,
    selectedProduct,
    allCategories,
    notifications,
  ])

  const [funnelData, setFunnelData] = useState<{
    views: number
    cart: number
    checkout: number
    orders: number
  } | null>(null)

  useEffect(() => {
    if (products.length > 0 && orders.length > 0) {
      setFunnelData({
        views: products.reduce((sum, p) => sum + (p.reviews || 0), 0) || 0,
        cart: products.reduce((sum, p) => sum + (p.commentCount || 0), 0) || 0,
        checkout: orders.length > 0 ? Math.floor(orders.length * 1.2) : 0,
        orders: orders.length,
      })
    } else {
      setFunnelData(null)
    }
  }, [products, orders])

  const [customerSegmentation, setCustomerSegmentation] = useState<{
    newCount: number
    returningCount: number
    repeatRate: number
    topSpenders: Array<{ id: string; name: string; totalSpent: number }>
  } | null>(null)

  useEffect(() => {
    if (users.length > 0 && orders.length > 0) {
      const userOrderCounts: Record<string, number> = {}
      orders.forEach((o) => {
        userOrderCounts[(o as any).userId] = (userOrderCounts[(o as any).userId] || 0) + 1
      })

      const newCount = Object.values(userOrderCounts).filter((c) => c === 1).length
      const returningCount = Object.values(userOrderCounts).filter((c) => c > 1).length
      const totalUniqueCustomers = Object.keys(userOrderCounts).length
      const repeatRate = totalUniqueCustomers > 0 ? (returningCount / totalUniqueCustomers) * 100 : 0

      const userSpend: Record<string, number> = {}
      orders.forEach((o) => {
        userSpend[(o as any).userId] = (userSpend[(o as any).userId] || 0) + o.total
      })

      const topSpenders = Object.entries(userSpend)
        .map(([id, totalSpent]) => ({
          id,
          name: users.find((u) => u.uid === id)?.name || id,
          totalSpent,
        }))
        .sort((a, b) => b.totalSpent - a.totalSpent)
        .slice(0, 5)

      setCustomerSegmentation({ newCount, returningCount, repeatRate, topSpenders })
    } else {
      setCustomerSegmentation(null)
    }
  }, [users, orders])

  const exportDashboardData = (type: "csv" | "excel" = "csv") => {
    const statsHeaders = [
      "Total Sales",
      "Today Sales",
      "Yesterday Sales",
      "Total Orders",
      "Total Products",
      "Total Users",
      "Pending Orders",
      "Completed Orders",
      "Low Stock Products",
      "Avg Order Value",
      "Conversion Rate",
      "Monthly Growth",
      "Daily Orders Avg",
      "Top Selling Category",
    ]
    const statsRow = [
      stats.totalSales,
      stats.todaySales,
      stats.yesterdaySales,
      stats.totalOrders,
      stats.totalProducts,
      stats.totalUsers,
      stats.pendingOrders,
      stats.completedOrders,
      stats.lowStockProducts,
      stats.avgOrderValue,
      stats.conversionRate,
      stats.monthlyGrowth,
      stats.dailyOrdersAvg,
      stats.topSellingCategory,
    ]

    const orderHeaders = [
      "Order ID",
      "User ID",
      "Customer Name",
      "Total",
      "Status",
      "Payment Method",
      "Payment Status",
      "Created At",
      "Updated At",
      "Shipping Name",
      "Shipping Address",
      "Shipping City",
      "Shipping State",
      "Shipping Pincode",
      "Shipping Phone",
      "Items",
    ]
    const orderRows = orders.map((order) => [
      order.id,
      (order as any).userId,
      order.shippingAddress?.name || "",
      order.total,
      order.status,
      order.paymentMethod || "",
      order.paymentStatus || "",
      order.createdAt,
      order.updatedAt || "",
      order.shippingAddress?.name || "",
      order.shippingAddress?.address || "",
      order.shippingAddress?.city || "",
      order.shippingAddress?.state || "",
      order.shippingAddress?.pincode || "",
      order.shippingAddress?.phone || "",
      order.items.map((i) => `${i.name || ""} (x${i.quantity}) @ ₹${i.price}`).join("; "),
    ])

    const productHeaders = [
      "Product ID",
      "Name",
      "Price",
      "Stock",
      "Category",
      "Categories (Tags)",
      "Reviews",
      "Rating",
      "Created At",
      "Description",
    ]
    const productRows = products.map((product) => [
      product.id,
      product.name,
      product.price,
      product.stock,
      product.category,
      product.categories?.join(", ") || "",
      product.reviews,
      product.rating,
      product.createdAt || "",
      product.description || "",
    ])

    const userHeaders = ["User ID", "Name", "Email", "Created At", "Last Login"]
    const userRows = users.map((user) => [
      user.uid,
      user.name,
      user.email,
      (user as any).createdAt || "",
      (user as any).updatedAt || "",
    ])

    if (type === "excel") {
      const wb = XLSX.utils.book_new()
      const wsStats = XLSX.utils.aoa_to_sheet([statsHeaders, statsRow])
      const wsOrders = XLSX.utils.aoa_to_sheet([orderHeaders, ...orderRows])
      const wsProducts = XLSX.utils.aoa_to_sheet([productHeaders, ...productRows])
      const wsUsers = XLSX.utils.aoa_to_sheet([userHeaders, ...userRows])

      XLSX.utils.book_append_sheet(wb, wsStats, "Summary")
      XLSX.utils.book_append_sheet(wb, wsOrders, "Orders")
      XLSX.utils.book_append_sheet(wb, wsProducts, "Products")
      XLSX.utils.book_append_sheet(wb, wsUsers, "Users")

      XLSX.writeFile(wb, `dashboard-data-${new Date().toISOString().split("T")[0]}.xlsx`)
      return
    }

    let csvContent = ""
    csvContent += "DASHBOARD SUMMARY" + "\n"
    csvContent += statsHeaders.join(",") + "\n"
    csvContent += statsRow.map((field) => `"${field}"`).join(",") + "\n\n"

    csvContent += "ORDERS" + "\n"
    csvContent += orderHeaders.join(",") + "\n"
    csvContent += orderRows.map((row) => row.map((field) => `"${field}"`).join(",")).join("\n") + "\n\n"

    csvContent += "PRODUCTS" + "\n"
    csvContent += productHeaders.join(",") + "\n"
    csvContent += productRows.map((row) => row.map((field) => `"${field}"`).join(",")).join("\n") + "\n\n"

    csvContent += "USERS" + "\n"
    csvContent += userHeaders.join(",") + "\n"
    csvContent += userRows.map((row) => row.map((field) => `"${field}"`).join(",")).join("\n")

    const blob = new Blob([csvContent], { type: "text/csv" })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `dashboard-data-${new Date().toISOString().split("T")[0]}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
  }

  const exportDashboardPDF = async () => {
    const dashboardElement = document.getElementById("dashboard-content")
    if (!dashboardElement) return

    const canvas = await html2canvas(dashboardElement, { scale: 2 })
    const imgData = canvas.toDataURL("image/png")

    const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" })
    const pageWidth = pdf.internal.pageSize.getWidth()
    const pageHeight = pdf.internal.pageSize.getHeight()

    const imgProps = pdf.getImageProperties(imgData)
    const pdfWidth = pageWidth - 40
    const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width

    const currentY = 20

    if (pdfHeight > pageHeight - 40) {
      let imgHeightLeft = imgProps.height
      const imgWidth = imgProps.width
      let pageNum = 1

      while (imgHeightLeft > 0) {
        const pageHeightForImage = pageHeight - 40
        const sHeight = (pageWidth * imgHeightLeft) / imgWidth

        if (sHeight > pageHeightForImage) {
          const cropHeight = (pageHeightForImage * imgWidth) / pageWidth
          const imgCanvas = document.createElement("canvas")
          imgCanvas.width = imgWidth
          imgCanvas.height = cropHeight
          const imgContext = imgCanvas.getContext("2d")
          imgContext?.drawImage(
            canvas,
            0,
            imgProps.height - imgHeightLeft,
            imgWidth,
            cropHeight,
            0,
            0,
            imgWidth,
            cropHeight,
          )
          const croppedImgData = imgCanvas.toDataURL("image/png")
          pdf.addImage(croppedImgData, "PNG", 20, 20, pdfWidth, pageHeightForImage, undefined, "FAST")
          imgHeightLeft -= cropHeight
        } else {
          const imgCanvas = document.createElement("canvas")
          imgCanvas.width = imgWidth
          imgCanvas.height = imgHeightLeft
          const imgContext = imgCanvas.getContext("2d")
          imgContext?.drawImage(
            canvas,
            0,
            imgProps.height - imgHeightLeft,
            imgWidth,
            imgHeightLeft,
            0,
            0,
            imgWidth,
            imgHeightLeft,
          )
          const croppedImgData = imgCanvas.toDataURL("image/png")
          pdf.addImage(croppedImgData, "PNG", 20, 20, pdfWidth, sHeight, undefined, "FAST")
          imgHeightLeft = 0
        }

        if (imgHeightLeft > 0) {
          pdf.addPage()
          pageNum++
        }
      }
    } else {
      pdf.addImage(imgData, "PNG", 20, 20, pdfWidth, pdfHeight, undefined, "FAST")
    }

    pdf.save(`dashboard-report-${new Date().toISOString().split("T")[0]}.pdf`)
  }

  const recentOrders = orders.slice(0, 5)
  const [showExportDropdown, setShowExportDropdown] = useState(false)
  const [widgets, setWidgets] = useState(getSavedWidgets())
  const [showCustomize, setShowCustomize] = useState(false)

  useEffect(() => {
    saveWidgets(widgets)
  }, [widgets])

  const handleDragEnd = (event: any) => {
    const { active, over } = event
    if (active.id !== over.id) {
      setWidgets((prev: any) => {
        const oldIdx = prev.findIndex((w: any) => w.id === active.id)
        const newIdx = prev.findIndex((w: any) => w.id === over.id)
        return arrayMove(prev, oldIdx, newIdx)
      })
    }
  }

  const isVisible = (id: string) => widgets.find((w: any) => w.id === id)?.visible

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard data...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto space-y-8" id="dashboard-content">
        {/* Header with Notification Bell */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
            <p className="text-gray-600 mt-1">Welcome back! Here's what's happening with your store today.</p>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            {/* Notification Bell */}
            <NotificationBell
              notifications={notifications}
              unreadCount={unreadCount}
              markAsRead={markAsRead}
              markAllAsRead={markAllAsRead}
              clearNotification={clearNotification}
            />

            {/* Time Filter */}
            <select
              value={timeFilter}
              onChange={(e) => setTimeFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="custom">Custom Range</option>
            </select>

            {timeFilter === "custom" && (
              <>
                <input
                  type="date"
                  value={customDateRange[0] ? customDateRange[0].toISOString().split("T")[0] : ""}
                  onChange={(e) =>
                    setCustomDateRange([e.target.value ? new Date(e.target.value) : null, customDateRange[1]])
                  }
                  className="px-2 py-2 border border-gray-300 rounded-lg"
                />
                <span className="mx-1">to</span>
                <input
                  type="date"
                  value={customDateRange[1] ? customDateRange[1].toISOString().split("T")[0] : ""}
                  onChange={(e) =>
                    setCustomDateRange([customDateRange[0], e.target.value ? new Date(e.target.value) : null])
                  }
                  className="px-2 py-2 border border-gray-300 rounded-lg"
                />
              </>
            )}

            {/* Category Filter */}
            <select
              value={selectedCategory}
              onChange={(e) => {
                setSelectedCategory(e.target.value)
                setSelectedProduct("")
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Categories</option>
              {allCategories.map((category) => (
                <option key={category.slug} value={category.slug}>
                  {category.name}
                </option>
              ))}
            </select>

            {/* Product Filter */}
            <select
              value={selectedProduct}
              onChange={(e) => setSelectedProduct(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={!selectedCategory && products.length === 0}
            >
              <option value="">All Products</option>
              {productsByCategory.map((product) => (
                <option key={product.id} value={product.id}>
                  {product.name}
                </option>
              ))}
            </select>

            {/* Export Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowExportDropdown(!showExportDropdown)}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <Download className="w-4 h-4" />
                Export
              </button>

              {showExportDropdown && (
                <div className="absolute right-0 mt-2 w-40 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                  <button
                    className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                    onClick={() => {
                      exportDashboardData("csv")
                      setShowExportDropdown(false)
                    }}
                  >
                    Export as CSV
                  </button>
                  <button
                    className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                    onClick={() => {
                      exportDashboardData("excel")
                      setShowExportDropdown(false)
                    }}
                  >
                    Export as Excel
                  </button>
                  <button
                    className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                    onClick={() => {
                      exportDashboardPDF()
                      setShowExportDropdown(false)
                    }}
                  >
                    Download PDF
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
          <h3 className="text-xl font-semibold text-gray-900 mb-6">Quick Actions</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4">
            <Link
              to="/admin/products/add"
              className="inline-flex flex-col items-center p-4 bg-primary-50 text-primary-700 rounded-lg hover:bg-primary-100 transition-colors"
            >
              <Plus className="w-6 h-6 mb-2" />
              <span className="text-sm font-medium">Add Product</span>
            </Link>
            <Link
              to="/admin/categories"
              className="inline-flex flex-col items-center p-4 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors"
            >
              <List className="w-6 h-6 mb-2" />
              <span className="text-sm font-medium">Categories</span>
            </Link>
            <Link
              to="/admin/orders"
              className="inline-flex flex-col items-center p-4 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors"
            >
              <ShoppingCart className="w-6 h-6 mb-2" />
              <span className="text-sm font-medium">Orders</span>
            </Link>
            <Link
              to="/admin/customers"
              className="inline-flex flex-col items-center p-4 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors"
            >
              <Users className="w-6 h-6 mb-2" />
              <span className="text-sm font-medium">Customers</span>
            </Link>
            <Link
              to="/admin/enquiries"
              className="inline-flex flex-col items-center p-4 bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 transition-colors"
            >
              <AlertCircle className="w-6 h-6 mb-2" />
              <span className="text-sm font-medium">Enquiries</span>
            </Link>
            <Link
              to="/admin/settings"
              className="inline-flex flex-col items-center p-4 bg-orange-50 text-orange-700 rounded-lg hover:bg-orange-100 transition-colors"
            >
              <Settings className="w-6 h-6 mb-2" />
              <span className="text-sm font-medium">Settings</span>
            </Link>
          </div>
        </div>

        {/* Stats Cards */}
        {isVisible("statCards") && <StatCards stats={stats} />}

        {/* Performance Metrics */}
        {isVisible("performanceMetrics") && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-lg font-semibold text-gray-900">Order Completion</h4>
                <CheckCircle className="w-5 h-5 text-green-500" />
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600 mb-2">
                  {((stats.completedOrders / stats.totalOrders) * 100 || 0).toFixed(1)}%
                </div>
                <div className="text-sm text-gray-600">Success Rate</div>
                <div className="mt-4 bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-green-500 h-2 rounded-full"
                    style={{ width: `${(stats.completedOrders / stats.totalOrders) * 100 || 0}%` }}
                  ></div>
                </div>
              </div>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-lg font-semibold text-gray-900">Avg Daily Revenue</h4>
                <TrendingUp className="w-5 h-5 text-blue-500" />
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-600 mb-2">
                  ₹{Math.round(stats.dailyOrdersAvg * stats.avgOrderValue).toLocaleString()}
                </div>
                <div className="text-sm text-gray-600">Per Day</div>
                <div className="mt-4">
                  <span className="text-xs text-gray-500">Based on {stats.dailyOrdersAvg.toFixed(1)} orders/day</span>
                </div>
              </div>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-lg font-semibold text-gray-900">Conversion Rate</h4>
                <Activity className="w-5 h-5 text-purple-500" />
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-purple-600 mb-2">{stats.conversionRate.toFixed(1)}%</div>
                <div className="text-sm text-gray-600">Users to Orders</div>
                <div className="mt-4 bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-purple-500 h-2 rounded-full"
                    style={{ width: `${Math.min(stats.conversionRate, 100)}%` }}
                  ></div>
                </div>
              </div>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-lg font-semibold text-gray-900">Monthly Growth</h4>
                <TrendingUp className="w-5 h-5 text-orange-500" />
              </div>
              <div className="text-center">
                <div
                  className={`text-3xl font-bold mb-2 ${stats.monthlyGrowth >= 0 ? "text-green-600" : "text-red-600"}`}
                >
                  {stats.monthlyGrowth >= 0 ? "+" : ""}
                  {stats.monthlyGrowth.toFixed(1)}%
                </div>
                <div className="text-sm text-gray-600">vs Yesterday</div>
                <div className="mt-4 flex items-center justify-center">
                  {stats.monthlyGrowth >= 0 ? (
                    <ArrowUpRight className="w-4 h-4 text-green-500" />
                  ) : (
                    <ArrowDownRight className="w-4 h-4 text-red-500" />
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Recent Orders & Top Customers */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Recent Orders Table */}
          {isVisible("recentOrders") && (
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-gray-900">Recent Orders</h3>
                <Link to="/admin/orders" className="text-primary-600 hover:text-primary-700 font-medium">
                  View all orders
                </Link>
              </div>
              <div className="overflow-x-auto">
                {recentOrders.length === 0 ? (
                  <div className="text-center py-8">
                    <ShoppingCart className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No orders yet</p>
                  </div>
                ) : (
                  <table className="w-full table-auto">
                    <thead>
                      <tr className="bg-gray-50 border-b">
                        <th className="text-left p-3 font-semibold text-gray-900 text-sm">Order</th>
                        <th className="text-left p-3 font-semibold text-gray-900 text-sm">Customer</th>
                        <th className="text-center p-3 font-semibold text-gray-900 text-sm">Status</th>
                        <th className="text-right p-3 font-semibold text-gray-900 text-sm">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentOrders.map((order) => (
                        <tr key={order.id} className="border-b hover:bg-gray-50">
                          <td className="p-3">
                            <div>
                              <div className="font-medium text-gray-900 text-sm">#{order.id.slice(-8)}</div>
                              <div className="text-xs text-gray-500">
                                {new Date(order.createdAt).toLocaleDateString()}
                              </div>
                            </div>
                          </td>
                          <td className="p-3">
                            <div className="text-sm text-gray-900">{order.shippingAddress?.name || "N/A"}</div>
                            <div className="text-xs text-gray-500">{order.items.length} items</div>
                          </td>
                          <td className="p-3 text-center">
                            <span
                              className={`inline-block px-2 py-1 text-xs rounded-full ${
                                order.paymentStatus === "completed"
                                  ? "bg-green-100 text-green-800"
                                  : order.paymentStatus === "failed"
                                    ? "bg-red-100 text-red-800"
                                    : order.paymentStatus === "half_paid"
                                      ? "bg-blue-100 text-blue-800"
                                      : "bg-yellow-100 text-yellow-800"
                              }`}
                            >
                              {order.paymentStatus === "half_paid"
                                ? "50% Advance Paid"
                                : order.paymentStatus}
                            </span>
                            {order.paymentMethod?.toLowerCase() === "cod" && order.codAdvanceAmount ? (
                              <div className="text-xs text-blue-600 mt-1">
                                Advance: ₹{order.codAdvanceAmount}
                              </div>
                            ) : null}
                          </td>
                          <td className="p-3 text-right font-semibold text-gray-900 text-sm">
                            ₹{order.total.toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}

          {/* Top Customers Table */}
          {isVisible("topCustomers") && (
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-gray-900">Top Customers</h3>
                <Users className="w-5 h-5 text-gray-400" />
              </div>
              <div className="overflow-x-auto">
                {topCustomers.length === 0 ? (
                  <div className="text-center py-8">
                    <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No customer data available</p>
                  </div>
                ) : (
                  <table className="w-full table-auto">
                    <thead>
                      <tr className="bg-gray-50 border-b">
                        <th className="text-left p-3 font-semibold text-gray-900 text-sm">Rank</th>
                        <th className="text-left p-3 font-semibold text-gray-900 text-sm">Customer</th>
                        <th className="text-right p-3 font-semibold text-gray-900 text-sm">Orders</th>
                        <th className="text-right p-3 font-semibold text-gray-900 text-sm">Spent</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topCustomers.map((customer, index) => (
                        <tr key={customer.id} className="border-b hover:bg-gray-50">
                          <td className="p-3">
                            <span className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-bold">
                              #{index + 1}
                            </span>
                          </td>
                          <td className="p-3">
                            <div>
                              <div className="font-medium text-gray-900 text-sm">{customer.name}</div>
                              <div className="text-xs text-gray-500 truncate">{customer.email}</div>
                            </div>
                          </td>
                          <td className="p-3 text-right text-gray-700 text-sm">{customer.totalOrders}</td>
                          <td className="p-3 text-right font-semibold text-gray-900 text-sm">
                            ₹{customer.totalSpent.toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Performance Alerts */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-gray-900">Performance Alerts</h3>
            <AlertCircle className="w-5 h-5 text-gray-400" />
          </div>
          <div className="space-y-4">
            {stats.lowStockProducts > 0 && (
              <div className="flex items-center p-4 bg-red-50 border border-red-200 rounded-lg">
                <AlertCircle className="w-5 h-5 text-red-500 mr-3" />
                <div className="flex-1">
                  <p className="font-medium text-red-800">Low Stock Alert</p>
                  <p className="text-sm text-red-600">
                    {stats.lowStockProducts} products have less than 20 items in stock
                  </p>
                </div>
                <Link
                  to="/admin/products"
                  className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                >
                  View Products
                </Link>
              </div>
            )}
            {stats.pendingOrders > 10 && (
              <div className="flex items-center p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <Clock className="w-5 h-5 text-yellow-500 mr-3" />
                <div className="flex-1">
                  <p className="font-medium text-yellow-800">Pending Orders Alert</p>
                  <p className="text-sm text-yellow-600">
                    You have {stats.pendingOrders} pending orders that need attention
                  </p>
                </div>
                <Link
                  to="/admin/orders"
                  className="px-4 py-2 bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200 transition-colors"
                >
                  View Orders
                </Link>
              </div>
            )}
            {stats.conversionRate < 5 && users.length > 0 && (
              <div className="flex items-center p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <TrendingUp className="w-5 h-5 text-blue-500 mr-3" />
                <div className="flex-1">
                  <p className="font-medium text-blue-800">Low Conversion Rate</p>
                  <p className="text-sm text-blue-600">
                    Your conversion rate is {stats.conversionRate.toFixed(1)}%. Consider improving your marketing
                    strategy.
                  </p>
                </div>
              </div>
            )}
            {stats.lowStockProducts === 0 && stats.pendingOrders <= 10 && stats.conversionRate >= 5 && (
              <div className="flex items-center p-4 bg-green-50 border border-green-200 rounded-lg">
                <Star className="w-5 h-5 text-green-500 mr-3" />
                <div className="flex-1">
                  <p className="font-medium text-green-800">All Systems Good!</p>
                  <p className="text-sm text-green-600">Your store is performing well with no critical alerts.</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Widget Customization Panel */}
      {showCustomize && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-xl shadow-xl max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Customize Dashboard</h3>
            <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableWidgetList widgets={widgets} setWidgets={setWidgets} />
            </DndContext>
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setShowCustomize(false)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Customize Button */}
      <button
        onClick={() => setShowCustomize(true)}
        className="fixed bottom-6 right-6 bg-primary-600 text-white p-3 rounded-full shadow-lg hover:bg-primary-700 transition-colors"
      >
        <Settings className="w-6 h-6" />
      </button>
    </div>
  )
}

const AdminDashboard: React.FC = () => (
  <ErrorBoundary>
    <AdminDashboardContent />
  </ErrorBoundary>
)

export default AdminDashboard

export interface Order {
  id: string;
  firebaseDocId?: string;
  items: any[];
  total: number;
  status: 'pending' | 'confirmed' | 'shipped' | 'delivered' | 'cancelled';
  paymentStatus: 'pending' | 'completed' | 'failed' | 'half_paid'; // <-- add "half_paid"
  paymentMethod?: string;
  codAdvanceAmount?: number; // <-- add this field
  shippingAddress: {
    name: string;
    phone: string;
    address: string;
    city: string;
    state: string;
    pincode: string;
  };
  createdAt: string;
  updatedAt?: string;
}
